import { INgRegistrationContext } from "./shared/types";
import ngRoute = angular.route;
import { setupExternalModuleHost } from "./external";
import { setupRoutes } from "./approutes";
import { isFunction } from "./shared/utils/utils";
import { loadScript } from "./shared/utils/loader";
import { getProperty } from "./shared/utils/objects";
import { v2Module as nutritionModuleV2 } from "./shared/nutrition/version/selector";
import { OmniaConfig } from "./types";

export function setupModules(ngmodule: ng.IModule, context: INgRegistrationContext, callback: () => void, config: OmniaConfig) {

    setupOnDemandModules(ngmodule, context, config)

    const preloadedModules = [
        "fields",
        "datalayers",
        "nutrition",
        "dashboard",
        "soil",
        "seeds",
        "stocks",
        "sync"
    ]

    const loadNext = () => {

        const m = preloadedModules.shift()

        if (m) {
            loadModule(ngmodule, loadNext, m)
        }
        else {
            callback()
        }

    }

    loadNext()

}


function setupOnDemandModules(ngmodule: ng.IModule, context: INgRegistrationContext, config: OmniaConfig) {

    const { features } = config

    const prescriptionPlansRoute = features.prescriptionPlans
        ? "prescriptions"
        : "7wsvpksz04"

    const modules = [

		{ name: "export", namespace: "exports", script: "exports", checkModule: (ns: any) => isFunction(ns["setup"])},
		{ name: "import", namespace: "imports", script: "imports", path: "/import/:component?/:resumeTask?" },
		{ name: "yield", namespace: "yield" },
		{ name: "seeds", namespace: "seeds" },
		{ name: "manure", directive: "omnia-mmp", namespace: "mmp", script: "mmp" },

        { name: "reports", external: true, path: "/reports", script: "reports", namespace: "reports" },
        { name: "reports2", external: true, path: "/reports-june-2025", script: "reports2", namespace: "reports2" },

        { name: "climate", external: true, path: "/climate" },

        { name: "diary", external: true, path: "/diary/:action?/:id?" },

        { name: "bp-shed", external: true, path: "/bp-shed", script: "bp-shed" },
        { name: "bp-prod", external: true, path: "/bp-prod", script: "bp-prod" },
        { name: "bp-plan", external: true, path: "/bp-plan", script: "bp-plan" },
        { name: "bp-perf", external: true, path: "/bp-perf", script: "bp-perf" },

        {
            name: "prescriptions",
            external: true,
            path: `/${prescriptionPlansRoute}/:action?/:id?`,
            checkModule: (ns: any) => isFunction(ns["start"])
        },

        { name: "protection", external: true, path: "/spray/:action?/:id?", checkModule: (ns: any) => isFunction(ns["start"]) },

        {
            name: "stocks2",
            external: true,
            path: "/stocks/:action?/:id?",
            checkModule: (ns: any) => isFunction(ns["start"])
        },


    ]
    .filter(Boolean)
    .map(d => getModule(context, d.name, d))
    .concat([

        nutritionModuleV2

    ])

    setupExternalModuleHost(ngmodule)

    ngmodule.config(["$routeProvider", ($routeProvider: ngRoute.IRouteProvider) => {

        setupRoutes($routeProvider, modules, config)

    }])

}

export type IAppModule = ReturnType<typeof getModule>

export function getModule(module: ng.IModule | INgRegistrationContext, name: string, {
	namespace = name,
	path = "/" + name,
	directive = "omnia-" + name,
	script = name,
	external = false,
	checkModule = (ns: any) => true
}) {

    const template = external
        ? "<omnia-external-module></omnia-external-module>"
        : `<${directive}></${directive}>`

    const loader = ($q: ng.IQService) => {

        const ns = window["omnia"][namespace]

        if (ns && checkModule(ns)) {
            return $q.resolve(ns)
        }

        const d = $q.defer()

        const callback = (component) => d.resolve(component)

		loadModule(module, callback, script, namespace)

        return d.promise

    }

    return {
        name,
        path,
        template,
        loader,
    }

}

function loadModule(module: ng.IModule | INgRegistrationContext, callback: (component: any) => void, name: string, namespace?: string) {

    namespace = namespace || name

	const script = `modules/omnia.${name}.js`

	loadOmniaComponent(module, namespace, script, callback)

}

export function loadOmniaComponent(context: INgRegistrationContext | ng.IModule, namespace: string, script: string, callback: (component: any) => void) {

    const ns = getNamespace(namespace)
    const setup = ns && ns.setup

    if (isFunction(setup)) {

        setup(context)

        if (callback) {
            callback(ns)
        }

    }

    else {

        const src = `built/${script}`

        loadScript(src, () => {

            const ns = getNamespace(namespace)

            ns.setup(context)

            if (callback) {
                callback(ns)
            }

        })

    }

}

function getNamespace(ns: string) {
    return getProperty(window["omnia"], ns)
}
